import { Card as CardId, Seat } from "@ravalmatic/bridge-engine/core";
import { GameVars } from "../../GameVars";
import { Card, CardPresentation } from "./Card";
import type { TrickCollectionCallbacks, TrickCollectionRequest } from "./BoardAnimations";

export class CardsContainer extends Phaser.GameObjects.Container {

    private static readonly COLLAPSE_DURATION = 168;
    private static readonly COLLECTION_DURATION = 420;
    private static readonly WINNER_DISPLAY_DURATION = 700;

    private trickKey = "";
    private trick: readonly { seat: Seat; card: CardId }[] = [];
    private readonly cardViews = new Map<CardId, Card>();
    private readonly movingCards = new Set<CardId>();
    private collection?: {
        request: TrickCollectionRequest;
        callbacks: TrickCollectionCallbacks;
        winnerCard: Card;
        started: boolean;
    };

    constructor(scene: Phaser.Scene) {

        super(scene);
        this.relayout();
    }

    public relayout(): void {

        this.setScale(1, GameVars.scaleY);
        if (!this.collection?.started) {
            this.setPosition(GameVars.gameWidth / 2, GameVars.gameHeight / 2 - (GameVars.isLandscape ? 0 : 169));
        }
    }

    public showTrick(
        trick: readonly { seat: Seat; card: CardId }[],
        getSource?: (
            seat: Seat, card: CardId, relativeTo: Phaser.GameObjects.Container
        ) => CardPresentation | undefined
    ): void {

        const key = trick.map(play => `${play.seat}:${play.card}`).join(",");
        if (key === this.trickKey) {
            return;
        }
        if (this.collection) {
            throw new Error("Cannot replace a trick while its collection is pending");
        }
        this.trickKey = key;
        this.trick = trick;
        for (const [cardId, view] of this.cardViews) {
            if (!trick.some(play => play.card === cardId)) {
                view.destroy();
                this.cardViews.delete(cardId);
                this.movingCards.delete(cardId);
            }
        }
        this.relayout();
        const positions = [
            { x: 0, y: -90 }, { x: 104, y: 0 },
            { x: 0, y: 90 }, { x: -104, y: 0 }
        ];
        trick.forEach(play => {
            if (this.cardViews.has(play.card)) {
                return;
            }
            const source = getSource?.(play.seat, play.card, this);
            const card = new Card(this.scene, play.card, source?.faceUp ?? true);
            const target = positions[play.seat];
            this.add(card);
            this.cardViews.set(play.card, card);
            if (source) {
                card.setPosition(source.x, source.y)
                    .setAngle(source.angle).setScale(source.scaleX, source.scaleY);
                card.flip(true);
                this.movingCards.add(play.card);
                this.scene.tweens.add({
                    targets: card,
                    x: target.x,
                    y: target.y,
                    angle: 0,
                    duration: 450,
                    ease: "Cubic.Out",
                    onComplete: () => {
                        if (!this.scene) {
                            return;
                        }
                        this.movingCards.delete(play.card);
                        this.tryCollect();
                    }
                });
            } else {
                card.setPosition(target.x, target.y);
            }
        });
    }

    public collectTrick(request: TrickCollectionRequest, callbacks: TrickCollectionCallbacks): void {

        if (this.collection) {
            const pending = this.collection.request;
            if (pending.token !== request.token || pending.trickNumber !== request.trickNumber
                || pending.winner !== request.winner || pending.kind !== request.kind) {
                throw new Error("Cannot replace a pending trick collection request");
            }
            return;
        }
        if (this.cardViews.size !== 4) {
            throw new Error("Trick collection requires four cards");
        }
        const winningPlay = this.trick.find(play => play.seat === request.winner);
        const winnerCard = winningPlay && this.cardViews.get(winningPlay.card);
        if (!winnerCard) {
            throw new Error("Trick collection requires a card for the winning seat");
        }
        this.collection = { request, callbacks, winnerCard, started: false };
        this.tryCollect();
    }

    public destroy(fromScene?: boolean): void {

        if (!this.scene) {
            return;
        }
        this.stopAnimations();
        this.cardViews.clear();
        super.destroy(fromScene);
    }

    public stopAnimations(): void {

        if (!this.scene) {
            return;
        }
        this.collection = undefined;
        this.scene.tweens.killTweensOf(this);
        this.movingCards.clear();
        this.cardViews.forEach(card => card.stopAnimations());
    }

    private tryCollect(): void {

        const collection = this.collection;
        if (!collection || collection.started || this.movingCards.size > 0) {
            return;
        }
        collection.started = true;
        collection.winnerCard.markAsWinner();
        let pending = this.cardViews.size;
        this.cardViews.forEach(card => {
            this.scene.tweens.add({
                targets: card,
                x: 0,
                y: 0,
                delay: CardsContainer.WINNER_DISPLAY_DURATION,
                duration: CardsContainer.COLLAPSE_DURATION,
                ease: "Cubic.Out",
                onComplete: () => {
                    if (!this.scene || this.collection !== collection) {
                        return;
                    }
                    pending--;
                    if (pending === 0) {
                        this.departTrick();
                    }
                }
            });
        });
    }

    private departTrick(): void {

        const collection = this.collection;
        if (!collection) {
            throw new Error("Trick departure requires a pending collection");
        }
        collection.callbacks.onCollapsed(collection.request);
        if (!this.scene || this.collection !== collection) {
            return;
        }
        const margin = Math.max(...Array.from(this.cardViews.values(), card =>
            Math.max(Card.WIDTH * Math.abs(card.scaleX * this.scaleX),
                Card.HEIGHT * Math.abs(card.scaleY * this.scaleY)) / 2
        )) + 16;
        const targets = [
            { x: this.x, y: -margin },
            { x: GameVars.gameWidth + margin, y: this.y },
            { x: this.x, y: GameVars.gameHeight + margin },
            { x: -margin, y: this.y }
        ];
        this.scene.tweens.add({
            targets: this,
            ...targets[collection.request.winner],
            delay: 150,
            duration: CardsContainer.COLLECTION_DURATION,
            ease: "Cubic.In",
            onComplete: () => {
                if (!this.scene || this.collection !== collection) {
                    return;
                }
                this.cardViews.forEach(card => card.destroy());
                this.cardViews.clear();
                this.trickKey = "";
                this.trick = [];
                this.collection = undefined;
                collection.callbacks.onComplete(collection.request);
            }
        });
    }
}
